<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStockItemPopup } from './useStockItemPopup';
import { getPostTypeLabel, isPointOutsideRect, rankByResultOrder } from './useCardUtils';
import type { SearchItem } from './useCardUtils';
import AiNavigationCardContent from './AiNavigationCardContent.vue';
import AiNavigationCardHeader from './AiNavigationCardHeader.vue';
import industryIconOther from './img/industry-icons/other.png';

interface ItemSetting {
  name: string;
  visible: boolean;
}

function resolveIndustryIconUrl(term: string | { name: string; iconUrl?: string }): string | null {
  if (typeof term === 'object' && typeof term.iconUrl === 'string' && term.iconUrl.trim() !== '') {
    return term.iconUrl.trim();
  }

  return null;
}

interface IndustryTerm {
  name: string;
  iconUrl?: string;
}

interface Props {
  apiEndpoint?: string;
  issueTerms?: string[];
  issueDisplayLabels?: Record<string, string>;
  industryTerms?: Array<string | IndustryTerm>;
  issueGenreMap?: Record<string, string[]>;
  contactformId?: string;
  contactformHtml?: string;
  industrySettings?: ItemSetting[];
  issueSettings?: ItemSetting[];
  resultOrder?: string[];
  industryInitialCount?: number;
  issueInitialCount?: number;
}

const STOCK_STORAGE_KEY = 'bizcan-ai-navigation-stock';
const POST_TYPE_FILTER_STORAGE_KEY = 'bizcan-ai-navigation-post-type-filter';
const STORAGE_TTL_MS = 60 * 60 * 1000;

function readWithTtl<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw);
    if (!envelope || typeof envelope.expiresAt !== 'number' || envelope.expiresAt <= Date.now()) {
      storage.removeItem(key);
      return null;
    }
    return envelope.data as T;
  } catch {
    return null;
  }
}

function writeWithTtl(storage: Storage, key: string, data: unknown): void {
  try {
    storage.setItem(key, JSON.stringify({ data, expiresAt: Date.now() + STORAGE_TTL_MS }));
  } catch {
    // storage unavailable
  }
}

const props = withDefaults(defineProps<Props>(), {
  apiEndpoint: '/wp-json/bizcan/v1/ai-recommend',
});

const isLoading = ref(false);
const searchItems = ref<SearchItem[]>([]);
const errorMessage = ref('');
const selectedIssues = ref<string[]>([]);
const selectedIndustries = ref<string[]>([]);
const selectedGenres = ref<string[]>([]);
const expandedCells = ref<string[]>([]);

// ===== STEP1/STEP2/CTA ゲーティング・「その他」展開（Figma準拠） =====
// STEP1（業種）・STEP2（課題）の初期表示件数は管理画面「コンテンツ検索設定」の区切り設定に従う。
// props未指定時は既存互換のフォールバック値（業種5・課題4、node-id 1:2196）を使う。
const industryInitialCount = computed(() => props.industryInitialCount ?? 5);
const issueInitialCount = computed(() => props.issueInitialCount ?? 4);
const isIndustryOthersExpanded = ref(false);
const isIssueOthersExpanded = ref(false);


const hasSearched = ref(false);
const hoveredItemId = ref<number | null>(null);
const hoveredCardHeight = ref<number>(0);
const disabledPostTypes = ref<string[]>(loadPostTypeFilter());

const selectedCardItem = ref<SearchItem | null>(null);

function openCardModal(item: SearchItem): void {
  selectedCardItem.value = item;
}

function closeCardModal(): void {
  selectedCardItem.value = null;
}

// ===== SP（タブレット含む）ビューポート判定 =====
const SP_BREAKPOINT = 1023;
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);
const isSpViewport = computed(() => viewportWidth.value <= SP_BREAKPOINT);
function onViewportResize(): void {
  viewportWidth.value = window.innerWidth;
}

// ===== 気になるリストの折りたたみ（SP） =====
const isStockExpanded = ref(false);
function toggleStockExpanded(): void {
  isStockExpanded.value = !isStockExpanded.value;
}

// ===== 検索条件折りたたみ（SP） =====
const isSearchConditionExpanded = ref(true);
function toggleSearchCondition(): void {
  isSearchConditionExpanded.value = !isSearchConditionExpanded.value;
}

const isSpConditionExpanded = computed(
  () => isSpViewport.value && searchItems.value.length > 0 && isSearchConditionExpanded.value
);

// ===== スクロール親への ref =====
const aiNavRootRef = ref<HTMLElement | null>(null);

// ===== 検索結果表示制御（SP） =====
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- setupState経由でテストから直接呼び出される公開API（tests/AiNavigationSpConditionCollapse.test.ts, tests/AiNavigationSpNoResults.test.ts）
async function showResults(): Promise<void> {
  isSearchConditionExpanded.value = false;
  await nextTick();
  if (aiNavRootRef.value) {
    aiNavRootRef.value.scrollTop = 0;
  }
}

// ===== ESCキーでカード詳細モーダルを閉じる =====
function onGlobalKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && selectedCardItem.value !== null) {
    closeCardModal();
  }
}

const availablePostTypes = computed<string[]>(() => {
  const set = new Set<string>();
  for (const item of searchItems.value) set.add(item.post_type);
  const types = Array.from(set);

  // resultOrder に基づいてソート（orderedSearchItems と同じロジック）
  const order =
    props.resultOrder && props.resultOrder.length > 0 ? props.resultOrder : DEFAULT_RESULT_ORDER;
  return types.sort((a, b) => rankByResultOrder(a, order) - rankByResultOrder(b, order));
});

const visibleSearchItems = computed<SearchItem[]>(() =>
  searchItems.value.filter((item) => !disabledPostTypes.value.includes(item.post_type)),
);

const DEFAULT_RESULT_ORDER = ['genre', 'feature', 'product'];

function normalizeTermName(term: string | IndustryTerm): string {
  return typeof term === 'string' ? term : term.name;
}

function applyItemSettings<T extends string | IndustryTerm>(
  terms: T[],
  settings?: ItemSetting[]
): T[] {
  if (!settings || settings.length === 0) return terms;
  const known = new Set(settings.map((s) => s.name));
  const ordered: T[] = [];
  for (const setting of settings) {
    const matching = terms.find((t) => normalizeTermName(t) === setting.name);
    if (setting.visible && matching) ordered.push(matching);
  }
  for (const term of terms) {
    if (!known.has(normalizeTermName(term))) ordered.push(term);
  }
  return ordered;
}

const orderedIndustryTerms = computed(() =>
  applyItemSettings(props.industryTerms ?? [], props.industrySettings),
);

const orderedIssueTerms = computed(() =>
  applyItemSettings(props.issueTerms ?? [], props.issueSettings),
);

// STEP2は業種（STEP1）を1件以上選択するまでロックする
const isStep2Active = computed(() => selectedIndustries.value.length > 0);

// CTAは業種・課題の両方を1件以上選択するまでロックする
const isCtaActive = computed(
  () => selectedIndustries.value.length > 0 && selectedIssues.value.length > 0,
);

const visibleIndustryTerms = computed(() =>
  isIndustryOthersExpanded.value || orderedIndustryTerms.value.length <= industryInitialCount.value
    ? orderedIndustryTerms.value
    : orderedIndustryTerms.value.slice(0, industryInitialCount.value),
);

const showIndustryOthersButton = computed(
  () =>
    !isIndustryOthersExpanded.value &&
    orderedIndustryTerms.value.length > industryInitialCount.value,
);

const visibleIssueTerms = computed<string[]>(() =>
  isIssueOthersExpanded.value || orderedIssueTerms.value.length <= issueInitialCount.value
    ? orderedIssueTerms.value
    : orderedIssueTerms.value.slice(0, issueInitialCount.value),
);

const showIssueOthersButton = computed(
  () => !isIssueOthersExpanded.value && orderedIssueTerms.value.length > issueInitialCount.value,
);

function expandIndustryOthers(): void {
  isIndustryOthersExpanded.value = true;
}

function expandIssueOthers(): void {
  isIssueOthersExpanded.value = true;
}

const orderedSearchItems = computed<SearchItem[]>(() => {
  const order =
    props.resultOrder && props.resultOrder.length > 0 ? props.resultOrder : DEFAULT_RESULT_ORDER;
  return [...visibleSearchItems.value].sort(
    (a, b) => rankByResultOrder(a.post_type, order) - rankByResultOrder(b.post_type, order),
  );
});

const refinementGenres = computed<string[]>(() => {
  if (!hasSearched.value || selectedIssues.value.length === 0) return [];
  const genres = new Set<string>();
  for (const issue of selectedIssues.value) {
    (props.issueGenreMap?.[issue] ?? []).forEach((g) => genres.add(g));
  }
  return Array.from(genres);
});

function loadPostTypeFilter(): string[] {
  const data = readWithTtl<string[]>(localStorage, POST_TYPE_FILTER_STORAGE_KEY);
  return Array.isArray(data) ? data : [];
}

function savePostTypeFilter(): void {
  if (disabledPostTypes.value.length === 0) {
    try {
      localStorage.removeItem(POST_TYPE_FILTER_STORAGE_KEY);
    } catch {
      // ignore
    }
    return;
  }
  writeWithTtl(localStorage, POST_TYPE_FILTER_STORAGE_KEY, disabledPostTypes.value);
}

function togglePostType(postType: string): void {
  if (disabledPostTypes.value.includes(postType)) {
    disabledPostTypes.value = disabledPostTypes.value.filter((t) => t !== postType);
  } else {
    disabledPostTypes.value = [...disabledPostTypes.value, postType];
  }
}


onMounted(() => {
  window.addEventListener('resize', onViewportResize);
  document.addEventListener('keydown', onGlobalKeydown);
});

onUnmounted(() => {
  window.removeEventListener('resize', onViewportResize);
  document.removeEventListener('keydown', onGlobalKeydown);
  document.removeEventListener('wpcf7mailsent', handleInquiryMailsent);
  document.removeEventListener('wpcf7beforesubmit', handleInquiryBeforeSubmit);
  document.removeEventListener('wpcf7invalid', handleInquirySubmitReset);
  document.removeEventListener('wpcf7spam', handleInquirySubmitReset);
  document.removeEventListener('wpcf7mailfailed', handleInquirySubmitReset);
});

const isInquiryModalOpen = ref(false);
const isInquirySubmitted = ref(false);
const inquiryFormContainer = ref<HTMLDivElement | null>(null);

type Wpcf7Init = { init?: (form: HTMLFormElement) => void };

function initCf7FormInModal(): void {
  const formEl = inquiryFormContainer.value?.querySelector('form');
  if (!(formEl instanceof HTMLFormElement)) return;
  const wpcf7 = (window as unknown as { wpcf7?: Wpcf7Init }).wpcf7;
  if (typeof wpcf7?.init === 'function') {
    wpcf7.init(formEl);
  }
}

function getInquirySubmitButton(): HTMLInputElement | null {
  return inquiryFormContainer.value?.querySelector('.wpcf7-submit') ?? null;
}

const stockedItems = ref<SearchItem[]>(loadStock());
const isDraggingOver = ref(false);
const draggingCardId = ref<number | null>(null);
const hoveredStockId = ref<number | null>(null);
const draggingStockId = ref<number | null>(null);
const stockZoneRef = ref<HTMLElement | null>(null);
const flashStockId = ref<number | null>(null);
let flashTimer: ReturnType<typeof setTimeout> | null = null;
const { stockPopupItemId, showPopup, hidePopup } = useStockItemPopup();

function loadStock(): SearchItem[] {
  const data = readWithTtl<SearchItem[]>(localStorage, STOCK_STORAGE_KEY);
  return Array.isArray(data) ? data : [];
}

function saveStock(): void {
  if (stockedItems.value.length === 0) {
    try {
      localStorage.removeItem(STOCK_STORAGE_KEY);
    } catch {
      // ignore
    }
    return;
  }
  writeWithTtl(localStorage, STOCK_STORAGE_KEY, stockedItems.value);
}


function clearSearch(): void {
  selectedIssues.value = [];
  selectedIndustries.value = [];
  selectedGenres.value = [];
  expandedCells.value = [];
  searchItems.value = [];
  hasSearched.value = false;
  errorMessage.value = '';
  stockedItems.value = [];
  isSearchConditionExpanded.value = true;
  try {
    localStorage.removeItem(STOCK_STORAGE_KEY);
  } catch {
    // storage unavailable
  }
}

function handleInquiryBeforeSubmit(event: Event): void {
  const customEvent = event as CustomEvent<{ contactFormId: string }>;
  if (String(customEvent.detail.contactFormId) === String(props.contactformId)) {
    const button = getInquirySubmitButton();
    if (button) {
      button.disabled = true;
    }
  }
}

function handleInquirySubmitReset(event: Event): void {
  const customEvent = event as CustomEvent<{ contactFormId: string }>;
  if (String(customEvent.detail.contactFormId) === String(props.contactformId)) {
    const button = getInquirySubmitButton();
    if (button) {
      button.disabled = false;
    }
  }
}

function handleInquiryMailsent(event: Event): void {
  const customEvent = event as CustomEvent<{ contactFormId: string }>;
  if (String(customEvent.detail.contactFormId) === String(props.contactformId)) {
    isInquirySubmitted.value = true;
  }
}

onMounted(() => {
  if (!props.contactformId) return;
  document.addEventListener('wpcf7mailsent', handleInquiryMailsent);
  document.addEventListener('wpcf7beforesubmit', handleInquiryBeforeSubmit);
  document.addEventListener('wpcf7invalid', handleInquirySubmitReset);
  document.addEventListener('wpcf7spam', handleInquirySubmitReset);
  document.addEventListener('wpcf7mailfailed', handleInquirySubmitReset);
});

watch(stockedItems, saveStock);

watch(disabledPostTypes, savePostTypeFilter, { deep: true });

function isStocked(postId: number): boolean {
  return stockedItems.value.some((s) => s.post_id === postId);
}

function stockItem(item: SearchItem): void {
  if (!isStocked(item.post_id)) {
    stockedItems.value = [...stockedItems.value, item];
    triggerStockFlash(item.post_id);
  }
}

function triggerStockFlash(postId: number): void {
  flashStockId.value = postId;
  if (flashTimer !== null) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => {
    flashStockId.value = null;
    flashTimer = null;
  }, 1000);
}

onUnmounted(() => {
  if (flashTimer !== null) {
    clearTimeout(flashTimer);
    flashTimer = null;
  }
});

function toggleStock(item: SearchItem): void {
  if (isStocked(item.post_id)) {
    removeFromStock(item.post_id);
  } else {
    stockItem(item);
  }
}

function removeFromStock(postId: number): void {
  stockedItems.value = stockedItems.value.filter((s) => s.post_id !== postId);
}

function onCardDragStart(event: DragEvent, item: SearchItem): void {
  event.dataTransfer?.setData('application/json', JSON.stringify(item));
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'copy';
  }
  draggingCardId.value = item.post_id;
}

function onCardDragEnd(): void {
  draggingCardId.value = null;
}

function onStockDragOver(): void {
  isDraggingOver.value = true;
}

function onStockDragLeave(): void {
  isDraggingOver.value = false;
}

function onStockDrop(event: DragEvent): void {
  isDraggingOver.value = false;
  const raw = event.dataTransfer?.getData('application/json');
  if (!raw) return;
  try {
    const item = JSON.parse(raw) as SearchItem;
    stockItem(item);
  } catch {
    // ignore invalid data
  }
}

function onStockItemDragStart(event: DragEvent, item: SearchItem): void {
  draggingStockId.value = item.post_id;
}

function onStockItemDragEnd(event: DragEvent): void {
  const id = draggingStockId.value;
  draggingStockId.value = null;
  if (id === null) return;
  const zone = stockZoneRef.value;
  if (!zone) return;
  const rect = zone.getBoundingClientRect();
  if (isPointOutsideRect(event.clientX, event.clientY, rect)) {
    removeFromStock(id);
  }
}

const canSearch = computed(() =>
  selectedIssues.value.length > 0 ||
  selectedIndustries.value.length > 0 ||
  selectedGenres.value.length > 0,
);

const loadingPhrase = computed(() => {
  const all = [
    ...selectedIssues.value,
    ...selectedIndustries.value,
    ...selectedGenres.value,
  ];
  if (all.length > 0) return all.slice(0, 3).join(' × ');
  return 'あなたの課題';
});

const productStockedItems = computed(() =>
  stockedItems.value.filter((item) => item.post_type === 'product'),
);

const genreStockedItems = computed(() =>
  stockedItems.value.filter((item) => item.post_type === 'genre'),
);

const featureStockedItems = computed(() =>
  stockedItems.value.filter((item) => item.post_type === 'feature'),
);

// 気になるリスト内のアイテムを「title（URL）」形式に整形するヘルパー関数
const formatStockedItems = (items: SearchItem[]): string =>
  items
    .map((item) => {
      const url = item.url.startsWith('/') ? `${window.location.origin}${item.url}` : item.url;
      return `${item.title}（${url}）`;
    })
    .join('\n');

const processedInquiryFormHtml = computed(() => {
  if (!props.contactformHtml) return '';
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;')
     .replace(/"/g, '&quot;')
     .replace(/'/g, '&#039;')
     .replace(/</g, '&lt;')
     .replace(/>/g, '&gt;')
     .replace(/\n/g, '&#10;');

  // 業種フィールド
  const industryField = selectedIndustries.value.length > 0
    ? `<input type="hidden" name="selected-industry" value="${escape(selectedIndustries.value.join('、'))}">`
    : '';

  // 課題フィールド
  const issueField = selectedIssues.value.length > 0
    ? `<input type="hidden" name="selected-issue" value="${escape(selectedIssues.value.join('、'))}">`
    : '';

  // 絞り込み（genre）フィールド
  const refinementField = selectedGenres.value.length > 0
    ? `<input type="hidden" name="selected-refinement" value="${escape(selectedGenres.value.join('、'))}">`
    : '';

  // 製品フィールド
  const productField = productStockedItems.value.length > 0
    ? `<input type="hidden" name="selected-product" value="${escape(formatStockedItems(productStockedItems.value))}">`
    : '';

  // ジャンルフィールド
  const genreField = genreStockedItems.value.length > 0
    ? `<input type="hidden" name="selected-genre" value="${escape(formatStockedItems(genreStockedItems.value))}">`
    : '';

  // 特集フィールド
  const featureField = featureStockedItems.value.length > 0
    ? `<input type="hidden" name="selected-feature" value="${escape(formatStockedItems(featureStockedItems.value))}">`
    : '';

  return props.contactformHtml.replace('</form>', `${industryField}${issueField}${refinementField}${productField}${genreField}${featureField}</form>`);
});

watch(
  [isInquiryModalOpen, processedInquiryFormHtml],
  async ([open]) => {
    if (open) {
      await nextTick();
      initCf7FormInModal();
    } else {
      isInquirySubmitted.value = false;
    }
  },
);

function toggleIssueTag(term: string): void {
  if (!isStep2Active.value) return;
  selectedIssues.value = selectedIssues.value.includes(term)
    ? selectedIssues.value.filter((t) => t !== term)
    : [...selectedIssues.value, term];
}

function toggleIndustryTag(term: string): void {
  selectedIndustries.value = selectedIndustries.value.includes(term)
    ? selectedIndustries.value.filter((t) => t !== term)
    : [...selectedIndustries.value, term];
}

function toggleGenreTag(genre: string): void {
  selectedGenres.value = selectedGenres.value.includes(genre)
    ? selectedGenres.value.filter((t) => t !== genre)
    : [...selectedGenres.value, genre];
  search();
}

async function search(): Promise<void> {
  if (!canSearch.value || isLoading.value) return;

  isLoading.value = true;
  errorMessage.value = '';
  hasSearched.value = true;

  try {
    const body: Record<string, string[]> = {};
    if (selectedIssues.value.length > 0) body.filter_search_issue = selectedIssues.value;
    if (selectedIndustries.value.length > 0) body.filter_industry = selectedIndustries.value;
    if (selectedGenres.value.length > 0) body.filter_genre_name = selectedGenres.value;

    const response = await fetch(props.apiEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      errorMessage.value = (typeof data === 'object' && data?.message)
        ? String(data.message)
        : '検索に失敗しました';
      searchItems.value = [];
    } else {
      const data = await response.json();
      searchItems.value = Array.isArray(data.items) ? data.items : [];
    }
  } catch {
    errorMessage.value = '通信エラーが発生しました。しばらくしてから再度お試しください。';
    searchItems.value = [];
  } finally {
    isLoading.value = false;
  }

  // SP幅で検索結果取得完了後、検索条件を自動的に折りたたむ
  if (isSpViewport.value) {
    isSearchConditionExpanded.value = false;
    nextTick().then(() => {
      if (aiNavRootRef.value) {
        aiNavRootRef.value.scrollTop = 0;
      }
    });
  }
}

// CTA「あなたに合うサービスを見る」クリック時のみ検索を実行する（STEP1・STEP2ゲート）
function onCtaClick(): void {
  if (!isCtaActive.value || isLoading.value) return;
  search();
}
</script>

<template>
  <div class="ai-navigation__root">
    <Teleport to="body" :disabled="true">
    <div class="ai-navigation__overlay">
      <div
        ref="aiNavRootRef"
        class="ai-navigation ai-navigation--inline"
        :class="{ 'is-sp-condition-expanded': isSpConditionExpanded }"
        aria-label="AI コンテンツ検索"
      >
        <div class="ai-navigation__header">
          <div class="ai-navigation__header-inner">
            <span class="ai-navigation__header-title">あなたのお店に合うサービスを<br class="ai-navigation__sp-line-break">カンタン検索</span>
            <p class="ai-navigation__header-subtitle">スグ見つかる！2つの質問に答えるだけ。</p>
          </div>
        </div>

        <!-- 本体（PC: コントロール上部＋カード/サイドバー2カラム / SP: 縦積み） -->
        <div
          class="ai-navigation__body"
          :class="{ 'ai-navigation__body--has-results': searchItems.length > 0 }"
        >
          <!-- 上部UI群（SPでは sticky・下スクロールで隠れ上スクロールで再表示） -->
          <div
            class="ai-navigation__sp-top"
          >
            <!-- サイドバー（気になるリスト）: 検索後のみ。SPでは上部に折りたたみ表示 -->
            <aside v-if="searchItems.length > 0 && !isSpViewport" class="ai-navigation__sidebar">
              <div
                ref="stockZoneRef"
                class="ai-navigation__stock-zone"
                :class="{
                  'is-drag-over': isDraggingOver,
                  'is-drag-active': draggingCardId !== null,
                  'has-items': stockedItems.length > 0,
                }"
                @dragover.prevent="onStockDragOver"
                @dragleave="onStockDragLeave"
                @drop.prevent="onStockDrop"
              >
                <button
                  type="button"
                  class="ai-navigation__stock-heading"
                  :aria-expanded="isStockExpanded"
                  @click="toggleStockExpanded"
                >
                  気になるリスト<span v-if="stockedItems.length">（{{ stockedItems.length }}件）</span>
                  <span
                    class="ai-navigation__stock-toggle"
                    :class="{ 'is-expanded': isStockExpanded }"
                    aria-hidden="true"
                  ></span>
                </button>
                <div class="ai-navigation__stock-body">
                  <template v-if="stockedItems.length === 0">
                    <div class="ai-navigation__stock-empty">
                      <span>気になったコンテンツを<br/>ここにドラッグ、または<br/><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/></svg>ボタンで追加</span>
                    </div>
                  </template>
                  <template v-else>
                    <div class="ai-navigation__stock-list">
                      <div
                        v-for="s in stockedItems"
                        :key="s.post_id"
                        class="ai-navigation__stock-item"
                        :class="{ 'is-flash': flashStockId === s.post_id }"
                        draggable="true"
                        @dragstart="onStockItemDragStart($event, s)"
                        @dragend="onStockItemDragEnd($event)"
                        @mouseenter="hoveredStockId = s.post_id"
                        @mouseleave="hoveredStockId = null; hidePopup()"
                      >
                        <div class="ai-navigation__stock-item-header">
                          <AiNavigationCardHeader :item="s" />
                        </div>
                        <div class="ai-navigation__stock-item-body">
                          <img
                            v-if="s.thumbnail_url"
                            :src="s.thumbnail_url"
                            :alt="s.title"
                            class="ai-navigation__stock-item-thumb"
                            draggable="false"
                          />
                          <a :href="s.url" class="ai-navigation__stock-item-title">{{ s.title }}</a>
                        </div>
                        <button
                          class="ai-navigation__stock-remove"
                          type="button"
                          :aria-label="`${s.title}をリストから削除`"
                          @click="removeFromStock(s.post_id)"
                        >×</button>
                        <div
                          v-if="stockPopupItemId === s.post_id && (s.long_excerpt || s.feature || s.excerpt)"
                          class="ai-navigation__stock-item-popup"
                          @mouseenter="showPopup(s.post_id)"
                          @mouseleave="hidePopup()"
                        >
                          <AiNavigationCardContent :item="s" variant="popup" :show-cta="false" />
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <button
                v-if="props.contactformId && props.contactformHtml && productStockedItems.length > 0"
                type="button"
                class="ai-navigation__inquiry-btn"
                @click="isInquiryModalOpen = true"
              >
                まとめてお問い合わせ（{{ productStockedItems.length }}件）
              </button>
            </aside>

            <!-- コントロール群（業種・課題・件数・絞り込み） -->
            <div
              class="ai-navigation__controls"
              :class="{ 'is-sp-expanded': isSpConditionExpanded }"
            >
              <!-- SP検索条件トグルバー -->
              <button
                v-if="isSpViewport && searchItems.length > 0"
                type="button"
                class="ai-navigation__sp-condition-toggle"
                :aria-expanded="isSearchConditionExpanded"
                @click="toggleSearchCondition"
              >
                <span class="ai-navigation__sp-condition-toggle-text">検索条件を指定</span>
                <span
                  class="ai-navigation__sp-condition-toggle-icon"
                  :class="{ 'is-open': isSearchConditionExpanded }"
                  aria-hidden="true"
                ></span>
              </button>

              <!-- 検索条件折りたたみラッパー（業種・課題・絞り込み） -->
              <div
                class="ai-navigation__condition-collapse"
                :class="{ 'is-collapsed': isSpViewport && searchItems.length > 0 && !isSearchConditionExpanded, 'is-sp-expanded': isSpConditionExpanded }"
              >
                <!-- タググリッド（業種・課題のみ） -->
                <div class="ai-navigation__tag-grid">
                  <!-- 業種を指定（STEP1: 常時活性） -->
                  <div
                    v-if="orderedIndustryTerms.length > 0"
                    class="ai-navigation__tag-section ai-navigation__tag-section--industry"
                  >
                    <div class="ai-navigation__tag-label">
                      <span class="ai-navigation__tag-step-badge is-active">STEP 1／2</span>
                      <span class="ai-navigation__tag-label-text">あなたの業種を選んでください</span>
                    </div>

                    <div class="ai-navigation__tag-cells">
                      <button
                        v-for="term in visibleIndustryTerms"
                        :key="`industry-${normalizeTermName(term)}`"
                        type="button"
                        class="ai-navigation__cell"
                        :class="{
                          'ai-navigation__cell--active': selectedIndustries.includes(normalizeTermName(term)),
                          'ai-navigation__cell--disabled': isLoading,
                        }"
                        :aria-pressed="selectedIndustries.includes(normalizeTermName(term))"
                        :disabled="isLoading"
                        @click="toggleIndustryTag(normalizeTermName(term))"
                      >
                        <img
                          v-if="resolveIndustryIconUrl(term)"
                          class="ai-navigation__cell-icon ai-navigation__cell-icon--taxonomy"
                          :src="resolveIndustryIconUrl(term) ?? undefined"
                          alt=""
                          aria-hidden="true"
                        />
                        <span class="ai-navigation__cell-text">{{ normalizeTermName(term) }}</span>
                      </button>
                      <button
                        v-if="showIndustryOthersButton"
                        type="button"
                        class="ai-navigation__cell ai-navigation__cell--expand"
                        @click="expandIndustryOthers"
                      >
                        <span
                          class="ai-navigation__cell-icon"
                          aria-hidden="true"
                          :style="{ '--cell-icon-src': `url(${industryIconOther})` }"
                        ></span>
                        <span class="ai-navigation__cell-text">その他</span>
                      </button>
                    </div>
                  </div>

                  <!-- 課題から探す（STEP2: 業種を1件以上選択するまでロック） -->
                  <div
                    v-if="orderedIssueTerms.length > 0"
                    class="ai-navigation__tag-section ai-navigation__tag-section--issue"
                    :class="{ 'ai-navigation__tag-section--disabled': !isStep2Active }"
                  >
                    <div class="ai-navigation__tag-label">
                      <span class="ai-navigation__tag-step-badge" :class="{ 'is-active': selectedIndustries.length > 0 }">STEP 2／2</span>
                      <span class="ai-navigation__tag-label-text">次に解決したい課題を選んでください</span>
                    </div>

                    <div class="ai-navigation__tag-cells">
                      <button
                        v-for="term in visibleIssueTerms"
                        :key="`issue-${term}`"
                        type="button"
                        class="ai-navigation__cell"
                        :class="{
                          'ai-navigation__cell--active': selectedIssues.includes(term),
                          'ai-navigation__cell--disabled': isLoading,
                          'ai-navigation__cell--locked': !isStep2Active,
                        }"
                        :aria-pressed="selectedIssues.includes(term)"
                        :disabled="isLoading || !isStep2Active"
                        @click="toggleIssueTag(term)"
                      >
                        <span
                          class="ai-navigation__cell-text"
                          :class="{ 'ai-navigation__cell-text--linebreak': (props.issueDisplayLabels?.[term] ?? term).includes('\n') }"
                        >
                          {{ props.issueDisplayLabels?.[term] ?? term }}
                        </span>
                      </button>
                      <button
                        v-if="showIssueOthersButton"
                        type="button"
                        class="ai-navigation__cell ai-navigation__cell--expand"
                        :class="{ 'ai-navigation__cell--locked': !isStep2Active }"
                        :disabled="!isStep2Active"
                        @click="expandIssueOthers"
                      >
                        <span
                          class="ai-navigation__cell-icon"
                          aria-hidden="true"
                          :style="{ '--cell-icon-src': `url(${industryIconOther})` }"
                        ></span>
                        <span class="ai-navigation__cell-text">その他の課題</span>
                      </button>
                    </div>
                  </div>

                  <!-- CTA（STEP1・STEP2を1件以上ずつ選択するまでロック） -->
                  <div class="ai-navigation__cta-wrap" :class="{ 'ai-navigation__cta-wrap--active': isCtaActive }">
                    <button
                      type="button"
                      class="ai-navigation__cta-btn"
                      :class="{ 'ai-navigation__cta-btn--active': isCtaActive }"
                      :disabled="!isCtaActive || isLoading"
                      @click="onCtaClick"
                    >
                      <span class="ai-navigation__cta-text">あなたに合う<br class="ai-navigation__sp-line-break">サービスを見る</span>
                    </button>
                    <!-- 無効時のみ表示：鍵アイコン + 案内文言 -->
                    <div v-if="!isCtaActive" class="ai-navigation__cta-note">
                      <span class="ai-navigation__cta-note-icon" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 15" width="10" height="15" fill="currentColor">
                          <path d="M5.90899 9.37493C5.90972 9.22154 5.87393 9.07032 5.80479 8.93452C5.73564 8.79871 5.63523 8.68248 5.51238 8.59602C5.38953 8.50955 5.24798 8.45549 5.10015 8.43858C4.95232 8.42167 4.80272 8.44242 4.66448 8.49901C4.52623 8.55561 4.40357 8.64632 4.30724 8.76319C4.21091 8.88006 4.14386 9.01952 4.11196 9.16934C4.08007 9.31916 4.08431 9.47476 4.12431 9.62249C4.16432 9.77023 4.23886 9.90559 4.3414 10.0167C4.38953 10.0693 4.42521 10.1326 4.44565 10.2018C4.46609 10.271 4.47073 10.3441 4.45922 10.4154L4.17286 12.1873H5.82676L5.5404 10.4154C5.52878 10.3441 5.53336 10.271 5.55376 10.2018C5.57416 10.1326 5.60984 10.0693 5.65799 10.0167C5.81825 9.8438 5.90809 9.6141 5.90899 9.37493Z"/>
                          <path d="M8.63636 5.625H8.18182V3.28125C8.18182 2.41101 7.84659 1.57641 7.24989 0.961056C6.65318 0.345702 5.84387 0 5 0C4.15613 0 3.34682 0.345702 2.75012 0.961056C2.15341 1.57641 1.81818 2.41101 1.81818 3.28125V5.625H1.36364C1.0021 5.62541 0.655484 5.7737 0.399838 6.03733C0.144193 6.30097 0.000397036 6.65841 0 7.03125V13.5938C0.000397036 13.9666 0.144193 14.324 0.399838 14.5877C0.655484 14.8513 1.0021 14.9996 1.36364 15H8.63636C8.9979 14.9996 9.34452 14.8513 9.60016 14.5877C9.85581 14.324 9.9996 13.9666 10 13.5938V7.03125C9.9996 6.65841 9.85581 6.30097 9.60016 6.03733C9.34452 5.7737 8.9979 5.62541 8.63636 5.625ZM2.72727 3.28125C2.72727 2.65965 2.96672 2.06351 3.39294 1.62397C3.81916 1.18443 4.39724 0.9375 5 0.9375C5.60277 0.9375 6.18084 1.18443 6.60706 1.62397C7.03328 2.06351 7.27273 2.65965 7.27273 3.28125V5.625H2.72727V3.28125ZM6.81195 12.5793C6.82278 12.6465 6.81931 12.7152 6.80177 12.7808C6.78422 12.8465 6.75304 12.9073 6.71038 12.9593C6.66771 13.0112 6.61459 13.0529 6.55472 13.0815C6.49484 13.1102 6.42963 13.125 6.36364 13.125H3.63636C3.57035 13.125 3.50512 13.1102 3.44522 13.0816C3.38532 13.053 3.33218 13.0113 3.28951 12.9594C3.24684 12.9074 3.21566 12.8465 3.19813 12.7809C3.18061 12.7153 3.17717 12.6465 3.18805 12.5793L3.52873 10.4718C3.33179 10.192 3.21369 9.86151 3.18749 9.51684C3.16129 9.17218 3.22801 8.82675 3.38027 8.51874C3.53254 8.21073 3.76441 7.95213 4.05028 7.77152C4.33614 7.59091 4.66487 7.49532 5.00011 7.49532C5.33536 7.49532 5.66408 7.59091 5.94995 7.77152C6.23581 7.95213 6.46769 8.21073 6.61995 8.51874C6.77222 8.82675 6.83893 9.17218 6.81274 9.51684C6.78654 9.86151 6.66844 10.192 6.4715 10.4718L6.81195 12.5793Z"/>
                        </svg>
                      </span>
                      <span class="ai-navigation__cta-note-text">業種と課題を選ぶと、ボタンが有効になります。</span>
                    </div>
                  </div>
                </div>

                <!-- 課題の個要素（絞り込み）: 一度検索した後に表示 -->
                <div
                  v-if="hasSearched && refinementGenres.length > 0"
                  class="ai-navigation__refinement"
                >
                  <span class="ai-navigation__refinement-label">絞り込み：</span>
                  <div class="ai-navigation__refinement-chips">
                    <button
                      v-for="genre in refinementGenres"
                      :key="genre"
                      type="button"
                      class="ai-navigation__chip ai-navigation__refinement-chip"
                      :class="{ 'ai-navigation__chip--active': selectedGenres.includes(genre) }"
                      :disabled="isLoading"
                      @click="toggleGenreTag(genre)"
                    >{{ genre }}</button>
                  </div>
                </div>

                <!-- 検索結果ヘッダー・フィルタ -->
                <template v-if="searchItems.length > 0">
                  <div class="ai-navigation__results-header">
                    <span class="ai-navigation__results-count">{{ visibleSearchItems.length }} 件</span>
                    <div class="ai-navigation__results-meta-group">
                      <span class="ai-navigation__results-meta">「{{ loadingPhrase }}」に関連するコンテンツ</span>
                    </div>
                    <button
                      v-if="selectedIssues.length > 0 || selectedIndustries.length > 0 || selectedGenres.length > 0 || stockedItems.length > 0"
                      type="button"
                      class="ai-navigation__clear-btn"
                      :disabled="isLoading"
                      @click="clearSearch"
                    >検索条件をクリア</button>
                  </div>

                  <div v-if="availablePostTypes.length > 0" class="ai-navigation__post-type-filter">
                    <button
                      v-for="postType in availablePostTypes"
                      :key="postType"
                      type="button"
                      class="ai-navigation__chip ai-navigation__post-type-chip"
                      :class="{ 'ai-navigation__chip--active': !disabledPostTypes.includes(postType) }"
                      :data-post-type="postType"
                      :aria-pressed="!disabledPostTypes.includes(postType)"
                      @click="togglePostType(postType)"
                    >
                      {{ getPostTypeLabel(postType) }}
                    </button>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <!-- ローディング -->
          <div v-if="isLoading" class="ai-navigation__loading" aria-live="polite">
            「{{ loadingPhrase }}」を検索中...
          </div>

          <!-- エラー -->
          <div v-if="errorMessage" class="ai-navigation__error">{{ errorMessage }}</div>

          <!-- 検索結果カード -->
          <div v-if="searchItems.length > 0 && !isSpConditionExpanded" class="ai-navigation__content-area">
            <div class="ai-navigation__results-column">
              <div class="ai-navigation__results">
                <article
                  v-for="(item, i) in orderedSearchItems"
                  :key="item.post_id"
                  :class="['ai-navigation__card', { 'is-popup-open': hoveredItemId === item.post_id, 'is-dragging': draggingCardId === item.post_id }]"
                  :style="{ animationDelay: (Math.floor(i / 2) * 0.08) + 's' }"
                  draggable="true"
                  @dragstart="onCardDragStart($event, item)"
                  @dragend="onCardDragEnd"
                  @mouseenter="(e: MouseEvent) => { if (isSpViewport) return; hoveredItemId = item.post_id; hoveredCardHeight = (e.currentTarget as HTMLElement).offsetHeight; }"
                  @mouseleave="hoveredItemId = null"
                >
                  <button
                    class="ai-navigation__card-stock-btn"
                    type="button"
                    :class="{ 'is-stocked': isStocked(item.post_id) }"
                    :title="isStocked(item.post_id) ? 'リストから削除' : '気になるリストに追加'"
                    :aria-label="isStocked(item.post_id) ? `${item.title}をリストから削除` : `${item.title}を気になるリストに追加`"
                    @click.stop="toggleStock(item)"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z"/>
                    </svg>
                  </button>

                  <div class="ai-navigation__card-body">
                    <AiNavigationCardContent
                      :item="item"
                      variant="card"
                      @detail="openCardModal(item)"
                    />
                  </div>

                  <div
                    v-if="hoveredItemId === item.post_id && (item.long_excerpt || item.feature || item.excerpt)"
                    class="ai-navigation__card-popup"
                    :style="hoveredCardHeight > 0 ? { maxHeight: hoveredCardHeight * 2 + 'px' } : {}"
                    @mouseenter="() => { if (isSpViewport) return; hoveredItemId = item.post_id; }"
                    @mouseleave="hoveredItemId = null"
                  >
                    <AiNavigationCardContent :item="item" variant="popup" />
                  </div>
                </article>
              </div>
            </div>
          </div>

          <!-- 検索済みで結果なし -->
          <div v-else-if="hasSearched && !isLoading && searchItems.length === 0" class="ai-navigation__no-results">
            該当するコンテンツが見つかりませんでした。条件を変えてお試しください。
          </div>

        <!-- SP下部 sticky 固定アクションバー -->
        <div
          v-if="(selectedIssues.length > 0 || selectedIndustries.length > 0 || selectedGenres.length > 0 || stockedItems.length > 0) && !(isSpViewport && isSearchConditionExpanded && visibleSearchItems.length > 0)"
          class="ai-navigation__sp-actionbar"
          :class="{ 'is-pre-search': searchItems.length === 0 }"
        >
          <!-- SP気になるリスト ドロワー（v-if=SP限定描画 / v-show=展開・折りたたみ制御） -->
          <div v-if="isSpViewport" v-show="isStockExpanded" class="ai-navigation__sp-stock-drawer">
            <div v-if="stockedItems.length === 0" class="ai-navigation__stock-empty">
              <span>気になるリストに追加されていません</span>
            </div>
            <div v-else class="ai-navigation__stock-list">
              <div
                v-for="s in stockedItems"
                :key="s.post_id"
                class="ai-navigation__stock-item"
              >
                <div class="ai-navigation__stock-item-header">
                  <AiNavigationCardHeader :item="s" />
                </div>
                <div class="ai-navigation__stock-item-body">
                  <img
                    v-if="s.thumbnail_url"
                    :src="s.thumbnail_url"
                    :alt="s.title"
                    class="ai-navigation__stock-item-thumb"
                    draggable="false"
                  />
                  <a :href="s.url" class="ai-navigation__stock-item-title">{{ s.title }}</a>
                </div>
                <button
                  class="ai-navigation__stock-remove"
                  type="button"
                  :aria-label="`${s.title}をリストから削除`"
                  @click="removeFromStock(s.post_id)"
                >×</button>
              </div>
            </div>
          </div>
          <div class="ai-navigation__sp-actionbar-row">
            <button
              type="button"
              class="ai-navigation__sp-stock-btn"
              :aria-expanded="isStockExpanded"
              @click="toggleStockExpanded"
            >
              気になるリスト<span v-if="stockedItems.length">（{{ stockedItems.length }}件）</span>
            </button>
            <button
              type="button"
              class="ai-navigation__sp-clear-btn"
              :disabled="isLoading"
              @click="clearSearch"
            >検索条件を<br>クリア</button>
          </div>
          <button
            v-if="props.contactformId && props.contactformHtml && productStockedItems.length > 0"
            type="button"
            class="ai-navigation__sp-inquiry-btn"
            @click="isInquiryModalOpen = true"
          >まとめてお問い合わせ（{{ productStockedItems.length }}件）</button>
        </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- お問い合わせモーダル -->
  <Teleport to="body">
    <div
      v-if="isInquiryModalOpen"
      class="ai-navigation__inquiry-modal-overlay"
      @click.self="isInquiryModalOpen = false"
    >
      <div class="ai-navigation__inquiry-modal" role="dialog" aria-modal="true" aria-label="まとめてお問い合わせ">
        <div class="ai-navigation__inquiry-modal-header">
          <span class="ai-navigation__inquiry-modal-title">まとめてお問い合わせ（{{ productStockedItems.length }}件）</span>
          <button
            type="button"
            class="ai-navigation__inquiry-modal-close"
            aria-label="モーダルを閉じる"
            @click="isInquiryModalOpen = false"
          >×</button>
        </div>
        <div class="ai-navigation__inquiry-modal-body">
          <template v-if="isInquirySubmitted">
            <p class="ai-navigation__inquiry-thanks">お問い合わせを送信しました。担当者よりご連絡いたします。</p>
          </template>
          <template v-else>
            <div class="ai-navigation__inquiry-products">
              <p class="ai-navigation__inquiry-products-label">お問い合わせ対象の製品</p>
              <ul class="ai-navigation__inquiry-product-list">
                <li v-for="item in productStockedItems" :key="item.post_id">{{ item.title }}</li>
              </ul>
            </div>
            <div ref="inquiryFormContainer" v-html="processedInquiryFormHtml"></div>
          </template>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- カード詳細モーダル -->
  <Teleport to="body">
    <div
      v-if="selectedCardItem"
      class="ai-navigation__card-detail-overlay"
      @click.self="closeCardModal"
    >
      <div class="ai-navigation__card-detail-modal" role="dialog" aria-modal="true" aria-label="コンテンツ詳細">
        <button
          type="button"
          class="ai-navigation__card-detail-close"
          aria-label="モーダルを閉じる"
          @click="closeCardModal"
        >×</button>
        <div class="ai-navigation__card-detail-header">
          <AiNavigationCardHeader :item="selectedCardItem" />
        </div>
        <div class="ai-navigation__card-detail-body">
          <AiNavigationCardContent :item="selectedCardItem" variant="detail" />
        </div>
      </div>
    </div>
  </Teleport>
  </div>
</template>

<style scoped>
/* ========================================
   モーダルオーバーレイ
   ======================================== */
.ai-navigation__overlay {
  display: contents;
}

/* ========================================
   モーダル本体
   ======================================== */
.ai-navigation {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.ai-navigation--inline {
  max-width: 1200px;
  margin: 0 auto;
  max-height: none;
  overflow: visible;
  /* 縦スクロール指定を打ち消し、横スクロールバーを抑止 */
  box-shadow: none;
  border: 2px solid #cce1e5;
  border-radius: 14px;
  padding: 16px;
}

/* ========================================
   ヘッダー・閉じるボタン
   ======================================== */
.ai-navigation__header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -24px -24px 20px;
  padding: 24px 24px 12px;
  border-bottom: 2px solid #cce1e5;
  background: #E9F9FA;
  border-radius: 14px 14px 0 0;
}

.ai-navigation--inline .ai-navigation__header {
  margin: -16px -16px 20px;
  padding: 16px 16px 12px;
  border-radius: 12px 12px 0 0;
}

.ai-navigation__header-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.ai-navigation__header-title {
  font-size: 22px;
  font-weight: 700;
  color: #333;
  text-align: center;
}

.ai-navigation__header-subtitle {
  font-size: 16px;
  font-weight: 400;
  color: #212121;
  text-align: center;
  margin: 0;
}

.ai-navigation__modal-close {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.ai-navigation--inline .ai-navigation__modal-close {
  right: 16px;
}

.ai-navigation__modal-close:hover {
  color: #F37422;
}

.ai-navigation__clear-btn {
  flex-shrink: 0;
  background: #fff;
  color: #333;
  border: 1px solid #36B3A7;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-navigation__sp-actionbar {
  display: none;
}

.ai-navigation__clear-btn:hover:not(:disabled) {
  background: #e0e0e0;
}

.ai-navigation__clear-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================
   検索条件折りたたみ
   ======================================== */
.ai-navigation__condition-collapse {
  overflow: hidden;
}

/* ========================================
   タググリッド・セクション
   ======================================== */
.ai-navigation__tag-grid {
  margin-bottom: 20px;
}

.ai-navigation__tag-section {
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 16px;
  margin-top: 18px;
  padding: 17px;
  padding-top: 30px;
  background: #fbfbfb;
  border: 2px solid #36B3A7;
  border-radius: 8px;
}

.ai-navigation__tag-section--disabled {
  border-color: #cce1e5;
}

.ai-navigation__tag-label {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0;
  height: 37px;
  background: #36B3A7; /* 角丸クリップ境界に露出する白い隙間を border と同色にして消す */
  border: 2px solid #36B3A7;
  border-radius: 7px;
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
  width: 662px;
  max-width: 100%;
  margin-left: 20px;
  margin-bottom: -18px;
  position: relative;
  z-index: 2;
}

.ai-navigation__tag-step-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #36B3A7;
  color: #fff;
  border: none;
  border-radius: 0;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
  width: 112px;
  align-self: stretch;
  padding: 0;
  margin: 0;
  letter-spacing: -0.5px;
  line-height: 1.2; /* 継承時 18px×1.8=32.4px → grid 行高膨張で label 内側 33px を超過し overflow:hidden クリップ */
}

.ai-navigation__tag-step-badge.is-active {
  background: #36B3A7;
  color: #fff;
}

.ai-navigation__tag-label-text {
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 20px;
  font-weight: 700;
  color: #36B3A7;
  align-self: stretch;
  white-space: nowrap;
  border-radius: 0 6px 6px 0;
  line-height: 1.2; /* 継承時 20px×1.8=36px → grid 行高膨張で label 内側 33px を超過し overflow:hidden クリップ */
  background: #fff;
}

.ai-navigation__tag-section--disabled .ai-navigation__tag-label {
  border-color: #cce1e5;
  background: #f0f7f6;
}

.ai-navigation__tag-section--disabled .ai-navigation__tag-step-badge {
  background: #cce1e5;
  color: #fff;
}

.ai-navigation__tag-section--disabled .ai-navigation__tag-label-text {
  color: #cce1e5;
  background: #f0f7f6;
}

.ai-navigation__sp-line-break {
  display: none;
}

.ai-navigation__tag-cells {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

/* ========================================
   セルボタン（業種・課題）
   ======================================== */
.ai-navigation__cell {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 17px;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 71px;
  background: #fff;
  border: 2px solid #cce1e5;
  color: #212121;
  padding: 17px 20px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  word-break: break-word;
  text-align: center;
}

.ai-navigation__cell-icon {
  display: block;
  flex: 0 0 auto;
  width: 37px;
  height: 37px;
}

/* タクソノミーSVGは未選択時にはアップロードされた配色・白抜きをそのまま表示する */
.ai-navigation__cell-icon--taxonomy {
  object-fit: contain;
}

/* 「その他」の固定画像だけはアルファマスクで状態色に追従させる */
.ai-navigation__cell-icon:not(.ai-navigation__cell-icon--taxonomy) {
  background-color: #cce1e5;
  -webkit-mask-image: var(--cell-icon-src);
  mask-image: var(--cell-icon-src);
  -webkit-mask-size: contain;
  mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-position: center;
  mask-position: center;
}

.ai-navigation__cell--active .ai-navigation__cell-icon:not(.ai-navigation__cell-icon--taxonomy) {
  background-color: #fff;
}

/* 選択中はSVG内の配色にかかわらずアイコン全体を白へ統一する */
.ai-navigation__cell--active .ai-navigation__cell-icon--taxonomy {
  filter: brightness(0) invert(1);
}

.ai-navigation__cell:hover:not(:disabled) {
  border-color: #36B3A7;
  background: #E9F9FA;
}

.ai-navigation__cell--active {
  background: #36B3A7;
  color: #fff;
  border-color: #36B3A7;
  transition: none;
}

.ai-navigation__cell--active:hover:not(:disabled) {
  background: #36B3A7;
  border-color: #36B3A7;
  color: #fff;
}

.ai-navigation__cell--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* STEP2ロック中（業種未選択）: グレーアウト表示。選択済み(--active)には適用しない */
.ai-navigation__cell--locked:not(.ai-navigation__cell--active) {
  color: #cce1e5;
  border-color: #cce1e5;
  cursor: not-allowed;
}

/* 「その他」「その他の課題」展開ボタン: 通常セルと同じ配色（Figma node1:10準拠、強調なし） */
.ai-navigation__cell--expand.ai-navigation__cell--locked {
  color: #cce1e5;
  border-color: #cce1e5;
}

.ai-navigation__cell-text {
  display: block;
}

.ai-navigation__cell-text--linebreak {
  white-space: pre;
  text-align: left;
}

/* ========================================
   CTA「あなたに合うサービスを見る」
   ======================================== */
.ai-navigation__cta-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: 8px 0 21px;
}

/* CTAボタンはPC/SP・活性/非活性に対応するSVG(cta-*.svg)を土台にする。
   枠・白い内枠・グラデーション・影・左装飾・矢印はSVGに内包済みのため、
   ボタン自体は透明な当たり判定+背景画像コンテナとし、文言のみ中央に重ねる。 */
.ai-navigation__cta-btn {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: none;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  color: #fff;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  cursor: not-allowed;
  transition: none;
}

.ai-navigation__cta-btn--active {
  cursor: pointer;
}

.ai-navigation__cta-text {
  text-align: center;
}

/* 無効時のCTA案内（鍵アイコン+文言） */
.ai-navigation__cta-note {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 15px 0;
}

.ai-navigation__cta-note-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #CCE1E5;
}

.ai-navigation__cta-note-icon svg {
  display: block;
}

.ai-navigation__cta-note-text {
  font-size: 16px;
  font-weight: 700;
  font-family: 'Noto Sans JP', sans-serif;
  color: #CCE1E5;
  text-align: center;
  line-height: 1.4;
}

/* ========================================
   絞り込みセクション
   ======================================== */
.ai-navigation__refinement {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #E9F9FA;
  border-radius: 8px;
  flex-wrap: wrap;
}

.ai-navigation__refinement-label {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
}

.ai-navigation__refinement-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ========================================
   チップボタン（投稿タイプ・ジャンル）
   ======================================== */
.ai-navigation__chip {
  background: #fff;
  border: 1px solid #e0e0e0;
  color: #333;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.ai-navigation__chip:hover:not(:disabled) {
  border-color: #36B3A7;
  background: #E9F9FA;
}

.ai-navigation__chip--active {
  background: #36B3A7;
  color: #fff;
  border-color: #36B3A7;
  transition: none;
}

.ai-navigation__chip--active:hover:not(:disabled) {
  background: #36B3A7;
  border-color: #36B3A7;
  color: #fff;
}

.ai-navigation__chip:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========================================
   ローディング・エラー
   ======================================== */
.ai-navigation__loading {
  padding: 16px;
  text-align: center;
  color: #666;
  font-size: 14px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.ai-navigation__error {
  padding: 16px;
  background: #ffebee;
  color: #c62828;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
  border: 1px solid #ef5350;
}

/* ========================================
   検索結果ヘッダー・フィルタ
   ======================================== */
.ai-navigation__results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 0 16px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.ai-navigation__results-count {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  flex-shrink: 0;
}

.ai-navigation__results-meta-group {
  flex: 1;
  min-width: 0;
}

.ai-navigation__results-meta {
  font-size: 13px;
  color: #666;
}

.ai-navigation__post-type-filter {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 16px;
  flex-wrap: wrap;
}

/* ========================================
   コンテンツエリア
   ======================================== */
/* 本体レイアウト: PCはコントロール上部＋カード/サイドバー2カラム。
   sp-top は PC では display:contents で子（sidebar/controls）をグリッドへ委譲し、
   SP では実体ブロック化して sticky のまとまりとして扱う。 */
.ai-navigation__sp-top {
  display: contents;
}

.ai-navigation__body--has-results {
  display: grid;
  grid-template-columns: 1fr 280px;
  grid-template-areas:
    "controls controls"
    "content  side";
  column-gap: 20px;
  align-items: start;
  padding-right: 16px;
  box-sizing: border-box;
}

.ai-navigation__body--has-results .ai-navigation__controls {
  grid-area: controls;
}

.ai-navigation__body--has-results .ai-navigation__sidebar {
  grid-area: side;
}

.ai-navigation__body--has-results .ai-navigation__content-area {
  grid-area: content;
}

/* 再検索中などグリッド有効時に状態表示が混在しても崩れないよう全幅化 */
.ai-navigation__body--has-results .ai-navigation__loading,
.ai-navigation__body--has-results .ai-navigation__error,
.ai-navigation__body--has-results .ai-navigation__no-results {
  grid-column: 1 / -1;
}

.ai-navigation__content-area {
  margin-bottom: 20px;
  min-width: 0;
  padding: 0 16px;
}

.ai-navigation--inline .ai-navigation__content-area {
  max-height: 480px;
  overflow-y: auto;
}

.ai-navigation__results-column {
  min-width: 0;
}

.ai-navigation__results {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

/* ========================================
   カード
   ======================================== */
.ai-navigation__card {
  position: relative;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 160px;
  animation: fadeInUp 0.5s ease-out both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-navigation__card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  border-color: #36B3A7;
}

.ai-navigation__card.is-dragging {
  opacity: 0.6;
  border-color: #F37422;
}

.ai-navigation__card.is-popup-open {
  z-index: 10;
  position: relative;
}


.ai-navigation__card-stock-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #36B3A7;
  border-radius: 4px;
  padding: 6px;
  display: flex;
  align-items: center;
  color: #36B3A7;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-navigation__card-stock-btn.is-stocked {
  color: #36B3A7;
  border-color: #36B3A7;
}

.ai-navigation__card-stock-btn.is-stocked svg {
  fill: #36B3A7;
}

@media (hover: hover) {
  .ai-navigation__card-stock-btn:hover {
    background: #fff;
    color: #F37422;
    border-color: #F37422;
  }

  .ai-navigation__card-stock-btn.is-stocked:hover {
    color: #2a9088;
    border-color: #2a9088;
  }
}

.ai-navigation__card-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.ai-navigation__card-popup {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-height: 200px;
}

/* ========================================
   サイドバー・ストックゾーン
   ======================================== */
.ai-navigation__sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-navigation__stock-zone {
  background: #fff;
  border: 2px dashed #36B3A7;
  border-radius: 8px;
  padding: 20px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;
  position: relative;
}

.ai-navigation__stock-zone.is-drag-active {
  border-color: #36B3A7;
}

.ai-navigation__stock-zone.is-drag-over {
  background: #D2F0EC;
  border-color: #36B3A7;
  border-style: solid;
  box-shadow: inset 0 0 0 2px rgba(54, 179, 167, 0.35);
}

.ai-navigation__stock-heading {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  text-align: center;
  /* 折りたたみトグル用にボタン化したためリセット */
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
}

/* 折りたたみトグルアイコン（SPのみ表示） */
.ai-navigation__stock-toggle {
  display: none;
}

/* 気になるリスト本体（折りたたみ対象） */
.ai-navigation__stock-body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.ai-navigation__stock-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #999;
  font-size: 13px;
  line-height: 1.6;
}

.ai-navigation__stock-empty svg {
  display: inline-block;
  vertical-align: middle;
}

.ai-navigation__stock-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.ai-navigation__stock-item {
  position: relative;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 12px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ai-navigation__stock-item:hover {
  background: #f5f5f5;
  border-color: #36B3A7;
}

/* 気になるリストに追加された直後のフラッシュ（枠線が緑にパルス） */
@keyframes stockFlash {
  0% {
    border-color: #36B3A7;
    box-shadow: 0 0 0 0 rgba(54, 179, 167, 0.6);
  }
  50% {
    border-color: #36B3A7;
    box-shadow: 0 0 0 4px rgba(54, 179, 167, 0.25);
  }
  100% {
    border-color: #e0e0e0;
    box-shadow: 0 0 0 0 rgba(54, 179, 167, 0);
  }
}

.ai-navigation__stock-item.is-flash {
  animation: stockFlash 1s ease-out;
}

.ai-navigation__stock-item-body {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.ai-navigation__stock-item-thumb {
  width: 40px;
  height: 40px;
  object-fit: contain;
  background: #fff;
  flex-shrink: 0;
  border-radius: 4px;
  display: block;
}

.ai-navigation__stock-item-header {
  padding-right: 22px;
}

/* 気になるリストのテキストサイズに合わせてヘッダーを縮小 */
.ai-navigation__stock-item-header :deep(.ai-navigation__card-header) {
  gap: 6px;
}

.ai-navigation__stock-item-header :deep(.ai-navigation__card-badge),
.ai-navigation__stock-item-header :deep(.ai-navigation__card-match) {
  font-size: 11px;
  padding: 2px 6px;
}

.ai-navigation__stock-item-header :deep(.ai-navigation__card-cta-primary),
.ai-navigation__stock-item-header :deep(.ai-navigation__card-cta-secondary) {
  font-size: 11px;
  padding: 3px 8px;
}

.ai-navigation__stock-item-title {
  display: block;
  color: #212121;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  word-break: break-word;
  margin-bottom: 4px;
  transition: color 0.2s ease;
  flex: 1;
  min-width: 0;
}

.ai-navigation__stock-item-title:hover {
  color: #00A0E9;
  text-decoration: underline;
}

.ai-navigation__stock-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  color: #999;
  font-size: 16px;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.ai-navigation__stock-remove:hover {
  color: #F37422;
}

.ai-navigation__stock-item-popup {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  min-height: 150px;
  margin-top: 4px;
}

/* ========================================
   お問い合わせボタン
   ======================================== */
.ai-navigation__inquiry-btn {
  width: 100%;
  background: #F37422;
  color: #fff;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.2);
}

.ai-navigation__inquiry-btn:hover {
  background: #D85E0F;
  box-shadow: 0 5px 12px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

/* ========================================
   お問い合わせモーダル
   ======================================== */
.ai-navigation__inquiry-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9002;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 24px;
}

.ai-navigation__inquiry-modal {
  position: relative;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.ai-navigation__inquiry-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.ai-navigation__inquiry-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  flex: 1;
}

.ai-navigation__inquiry-modal-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #333;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.ai-navigation__inquiry-modal-close:hover {
  color: #F37422;
}

.ai-navigation__inquiry-modal-body {
  padding: 12px 0;
}

.ai-navigation__inquiry-modal-body :deep(.wpcf7) {
  padding-top: 8px;
  padding-bottom: 8px;
}

.ai-navigation__inquiry-thanks {
  padding: 16px;
  background: #e8f5e9;
  color: #2e7d32;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

.ai-navigation__inquiry-products {
  margin-bottom: 20px;
}

.ai-navigation__inquiry-products-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.ai-navigation__inquiry-product-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.ai-navigation__inquiry-product-list li {
  padding: 8px 12px;
  background: #f9f9f9;
  border-left: 3px solid #36B3A7;
  margin-bottom: 6px;
  font-size: 12px;
  color: #333;
}

/* ========================================
   カード詳細モーダル
   ======================================== */
.ai-navigation__card-detail-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9002;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.ai-navigation__card-detail-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  height: 400px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ai-navigation__card-detail-close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #333;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: color 0.2s ease;
}

.ai-navigation__card-detail-close:hover {
  color: #F37422;
}

.ai-navigation__card-detail-header {
  flex-shrink: 0;
  padding: 12px 48px 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
}

.ai-navigation__card-detail-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
}

/* ========================================
   検索結果なしメッセージ
   ======================================== */
.ai-navigation__no-results {
  padding: 32px;
  text-align: center;
  color: #666;
  font-size: 14px;
  background: #f9f9f9;
  border-radius: 8px;
}

/* Figma node 1:2045 のPC固定フレーム。各寸法は同一コンポーネント内の
   構造関係として定義し、STEPラベルを各パネル上辺へ重ねる。 */
@media (min-width: 1024px) {
  .ai-navigation {
    font-family: 'Noto Sans JP', sans-serif;
  }

  .ai-navigation--inline {
    padding: 0;
  }

  .ai-navigation--inline .ai-navigation__header {
    box-sizing: border-box;
    /* ヘッダー帯を枠線の内側に収める（ルート非スクロールコンテナ化前の可視矩形と同寸） */
    height: 92px;
    margin: 0 0 20px;
    padding: 0;
    border-radius: 12px 12px 0 0;
  }

  .ai-navigation__header {
    align-items: flex-start;
  }

  .ai-navigation__header-inner {
    gap: 5px;
    padding-top: 10px;
  }

  .ai-navigation__header-title {
    line-height: 33px;
  }

  .ai-navigation__header-subtitle {
    line-height: 24px;
  }

  .ai-navigation__tag-grid {
    margin-bottom: 0;
  }

  .ai-navigation__tag-section {
    position: relative;
    width: calc(100% - 36px);
    margin: 19px 18px 0;
    padding: 26px 18px 17px;
  }

  .ai-navigation__tag-section + .ai-navigation__tag-section {
    margin-top: 51px;
  }

  .ai-navigation__tag-label {
    position: absolute;
    top: -21px;
    left: 18px;
    margin: 0;
  }

  .ai-navigation__tag-label-text {
    box-sizing: border-box;
    min-width: 0;
  }

  .ai-navigation__tag-cells {
    margin-bottom: 0;
  }

  .ai-navigation__cell {
    height: 71px;
    min-height: 71px;
    padding: 8px 12px;
    font-family: inherit;
    line-height: 1.25;
  }

  /* Figma 1:3096 / 1:2124: STEP1・STEP2の選択肢は残り幅へ伸ばさず内容幅で並べる */
  .ai-navigation__tag-section--industry .ai-navigation__cell,
  .ai-navigation__tag-section--issue .ai-navigation__cell {
    flex: 0 1 auto;
    width: fit-content;
  }

  .ai-navigation__tag-section--industry .ai-navigation__cell {
    max-width: 290px;
    padding-inline: 17px;
  }

  .ai-navigation__tag-section--issue .ai-navigation__cell {
    max-width: 216px;
  }

  .ai-navigation__tag-section--issue .ai-navigation__cell:not(.ai-navigation__cell--expand) {
    padding-inline: 36px;
  }

  .ai-navigation__tag-section--issue .ai-navigation__cell--expand {
    padding-inline: 17px;
  }

  .ai-navigation__cta-wrap {
    margin: 29px 0 21px;
    gap: 4px;
  }

  .ai-navigation__cta-note {
    padding-right: 10px;
  }

  .ai-navigation__cta-note-text {
    letter-spacing: 1.1px;
  }

  .ai-navigation__cta-btn {
    display: grid;
    grid-template-rows: 23fr 94fr 7fr;
    justify-items: center;
    width: 100%;
    max-width: 984px;
    aspect-ratio: 984 / 124;
    background-image: url('./img/cta-disable.svg');
    font-size: 36px;
  }

  .ai-navigation__cta-btn--active {
    background-image: url('./img/cta-active.svg');
  }

  .ai-navigation__cta-text {
    grid-row: 2;
    align-self: center;
    justify-self: center;
  }
}

/* ========================================
   レスポンシブ
   ======================================== */
@media (max-width: 1023px) {
  .ai-navigation {
    max-height: 75vh;
    max-height: 75dvh;
    max-width: 100%;
    padding: 15px;
    overflow-y: auto;
  }

  .ai-navigation--inline {
    max-height: none;
    overflow: visible;
    padding: 15px;
    /* モーダル用の縦スクロール・余白設定を明示的に上書き */
  }

  .ai-navigation--inline .ai-navigation__header {
    /* ルートの余白 15px と一致させ横はみ出しを解消 */
    margin: -15px -15px 20px;
    padding: 15px 15px 12px;
  }

  .ai-navigation__clear-btn {
    display: none;
  }

  .ai-navigation__sp-condition-toggle {
    position: relative;
    z-index: 30;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    background: #fff;
    border: none;
    border-bottom: 1px solid #e0e0e0;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  .ai-navigation__sp-condition-toggle-icon {
    width: 10px;
    height: 10px;
    border-right: 2px solid #333;
    border-bottom: 2px solid #333;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
  }

  .ai-navigation__sp-condition-toggle-icon.is-open {
    transform: rotate(-135deg);
  }

  .ai-navigation__sp-actionbar {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    background: #fff;
    padding: 8px 0;
    box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.08);
  }

  .ai-navigation__sp-actionbar-row {
    display: flex;
    gap: 8px;
  }

  .ai-navigation__sp-stock-btn {
    flex: 6;
    padding: 10px 8px;
    border: 1px solid #36B3A7;
    border-radius: 8px;
    background: #fff;
    color: #36B3A7;
    font-weight: bold;
    cursor: pointer;
  }

  .ai-navigation__sp-clear-btn {
    flex: 3;
    padding: 10px 8px;
    border: 1px solid #999999;
    border-radius: 8px;
    background: #fff;
    color: #999999;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
  }

  .ai-navigation__sp-inquiry-btn {
    width: 100%;
    padding: 12px 16px;
    border: none;
    border-radius: 8px;
    background: #F37422;
    color: #fff;
    font-weight: bold;
    cursor: pointer;
  }

  .ai-navigation__sp-stock-drawer {
    max-height: 50vh;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    margin-bottom: 8px;
    background: #F5F5F5;
    border: 1px dashed #36B3A7;
    border-radius: 8px;
  }

  .ai-navigation__condition-collapse {
    max-height: 1000px;
    transition: max-height 0.2s ease;
  }

  .ai-navigation__condition-collapse.is-collapsed {
    max-height: 0;
  }

  .ai-navigation__condition-collapse.is-sp-expanded {
    max-height: none;
    overflow: visible;
    padding-bottom: 24px;
  }

  /* SP: 縦積みレイアウト（PCの2カラムグリッドを解除） */
  .ai-navigation__body--has-results {
    display: block;
    padding-right: 0;
  }


  /* SP: 検索結果枠を画面高さの80%以内に制限し、カードのみ内部スクロールさせる */
  .ai-navigation--inline .ai-navigation__content-area {
    max-height: 80vh;
    max-height: 80dvh;
    overflow-y: auto;
  }

  .ai-navigation__results {
    grid-template-columns: 1fr;
  }

  .ai-navigation__sidebar {
    min-height: auto;
    margin-bottom: 0;
  }

  .ai-navigation__stock-zone {
    min-height: 200px;
    padding: 12px;
    position: static;
  }

  .ai-navigation__controls {
    position: relative;
    z-index: 20;
    background: #fff;
  }

  .ai-navigation__controls.is-sp-expanded {
    position: static;
  }

  .ai-navigation__controls.is-sp-expanded .ai-navigation__sp-condition-toggle {
    position: static;
  }

  /* SP: 業種・課題指定を一回り小さく */
  .ai-navigation__tag-label {
    position: relative;
    /* 親.ai-navigation__condition-collapse(overflow:hidden)の境界内に上側ボーダー(2px)を収める計算値（Figma座標の転写ではない）。下 12px はステップラベルと選択項目の間隔 */
    margin: -19px 0 12px;
    height: auto;
    min-height: 36px;
    font-size: 13px;
  }

  .ai-navigation__tag-step-badge {
    width: 100px;
    height: auto;
    align-self: stretch;
    box-sizing: border-box;
    padding: 8px;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: 0;
  }

  .ai-navigation__sp-line-break {
    display: initial;
  }

  .ai-navigation__tag-cells {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 6px;
  }

  .ai-navigation__cell {
    flex: 0 0 auto;
    width: fit-content;
    max-width: 100%;
    min-height: 40px;
    padding: 2px 5px;
    font-size: 12px;
    line-height: 1.2;
    gap: 4px;
  }

  .ai-navigation__cell-icon {
    width: 14px;
    height: 14px;
  }

  .ai-navigation__tag-section {
    position: relative;
    width: 100%;
    padding: 0 8px 8px;
  }

  .ai-navigation__tag-section + .ai-navigation__tag-section {
    margin-top: 33px;
  }

  .ai-navigation__tag-grid {
    margin-bottom: 14px;
  }

  .ai-navigation__cta-wrap {
    margin-top: 27px;
    margin-bottom: 24px;
    gap: 4px;
  }

  .ai-navigation__cta-note {
    padding: 7px 0;
    position: relative;
    left: -4px;
    top: 3px;
  }

  .ai-navigation__cta-note-text {
    font-size: 15px;
  }

  .ai-navigation__tag-label-text {
    height: auto;
    min-height: 36px;
    padding: 4px 8px;
    font-size: 14px;
    line-height: 1.3;
    white-space: normal;
  }

  .ai-navigation__cta-btn {
    width: 100%;
    max-width: 340px;
    aspect-ratio: 340 / 133;
    margin-inline: auto;
    background-image: url('./img/cta-disable-sp.svg');
    font-size: 26px;
  }

  .ai-navigation__cta-btn--active {
    background-image: url('./img/cta-active-sp.svg');
  }
}

@media (max-width: 600px) {
  .ai-navigation {
    padding: 8px;
    border-radius: 8px;
  }

  .ai-navigation__header {
    margin-bottom: 16px;
    padding-bottom: 8px;
  }

  .ai-navigation__header-title {
    font-size: 16px;
  }

  .ai-navigation__header-subtitle {
    font-size: 14px;
  }

  .ai-navigation--inline .ai-navigation__header {
    box-sizing: border-box;
    height: 98px;
    min-height: 98px;
    margin: -8px -8px 16px;
    padding: 12px 12px 8px;
  }

  .ai-navigation__results {
    grid-template-columns: 1fr;
  }

  .ai-navigation__modal-close,
  .ai-navigation__inquiry-modal-close {
    width: 28px;
    height: 28px;
    font-size: 20px;
  }

  .ai-navigation__inquiry-modal {
    max-width: 100%;
    padding: 16px;
  }

  .ai-navigation__results-count {
    font-size: 16px;
  }

  .ai-navigation__results-meta {
    font-size: 12px;
  }

  .ai-navigation__tag-section {
    padding: 0 8px 8px;
  }

  .ai-navigation__tag-cells {
    gap: 5px;
  }

  .ai-navigation__cell {
    min-height: 40px;
    padding: 2px 5px;
  }

  .ai-navigation__sp-actionbar.is-pre-search {
    display: none;
  }
}

</style>
